import React, { useEffect, useState } from 'react';
import { paymentsAPI } from '../services/api';
import { ModernCard, ModernStatCard, ModernLoadingSpinner } from '../components/ui/ModernComponents';
import { Pie } from 'react-chartjs-2';
import { Coins, CheckCircle, AlertCircle, CreditCard, Banknote } from 'lucide-react';

const PaiementsVueDEnsemble = () => {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ total: 0, paid: 0, partial: 0, unpaid: 0, byType: {}, byMode: {} });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await paymentsAPI.exportPaymentsJson();
        const data = res.data;
        setPayments(data);
        // Compute summary
        let total = 0, paid = 0, partial = 0, unpaid = 0;
        const byType = {}, byMode = {};
        data.forEach((p) => {
          const t = Number(p.reference_total) || null;
          const paidAmt = Number(p.amount);
          total += paidAmt;
          if (t) {
            if (paidAmt >= t) paid++;
            else if (paidAmt > 0) partial++;
            else unpaid++;
          }
          byType[p.reference_type] = (byType[p.reference_type] || 0) + paidAmt;
          byMode[p.mode] = (byMode[p.mode] || 0) + paidAmt;
        });
        setSummary({ total, paid, partial, unpaid, byType, byMode });
      } catch {
        setPayments([]);
        setSummary({ total: 0, paid: 0, partial: 0, unpaid: 0, byType: {}, byMode: {} });
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  // Prepare chart data
  const pieData = {
    labels: Object.keys(summary.byMode),
    datasets: [
      {
        data: Object.values(summary.byMode),
        backgroundColor: ['#22c55e', '#3b82f6', '#f59e42'],
        borderWidth: 1,
      },
    ],
  };

  // Compute summary by reference type label
  const byTypeLabel = {};
  payments.forEach((p) => {
    const label = p.reference_type_label || (p.reference_type === 'sales_order' ? 'Commande' : p.reference_type === 'sale' ? 'Vente directe' : p.reference_type === 'sales_invoice' ? 'Facture' : p.reference_type);
    byTypeLabel[label] = (byTypeLabel[label] || 0) + Number(p.amount);
  });

  return (
    <div className="space-y-8 p-6">
      <h1 className="text-2xl font-bold mb-4">Vue d'ensemble des paiements</h1>
      {loading ? (
        <ModernLoadingSpinner size="lg" />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <ModernStatCard title="Total Paiements" value={payments.length} icon={<Coins className="w-7 h-7 text-primary-600" />} variant="primary" />
            <ModernStatCard title="Total Montant" value={summary.total.toLocaleString('fr-FR', { style: 'currency', currency: 'MAD' })} icon={<CheckCircle className="w-7 h-7 text-success-600" />} variant="success" />
            <ModernStatCard title="Payés" value={summary.paid} icon={<CheckCircle className="w-7 h-7 text-success-600" />} variant="success" />
            <ModernStatCard title="Non Payés" value={summary.unpaid} icon={<AlertCircle className="w-7 h-7 text-danger-600" />} variant="danger" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <ModernCard className="p-6 flex flex-col items-center justify-center">
              <h2 className="text-lg font-semibold mb-4">Répartition par mode de paiement</h2>
              <div className="flex flex-row items-center justify-center gap-6 mb-4">
                <div className="flex items-center gap-2"><span className="inline-block w-5 h-3 rounded bg-[#22c55e]"></span><span>espece</span></div>
                <div className="flex items-center gap-2"><span className="inline-block w-5 h-3 rounded bg-[#3b82f6]"></span><span>carte_bancaire</span></div>
                <div className="flex items-center gap-2"><span className="inline-block w-5 h-3 rounded bg-[#f59e42]"></span><span>cheque</span></div>
              </div>
              <div style={{ maxWidth: 320, maxHeight: 320, margin: '0 auto' }}>
                <Pie data={pieData} options={{ maintainAspectRatio: false, responsive: true, plugins: { legend: { display: false } } }} height={220} width={220} />
              </div>
            </ModernCard>
            <ModernCard className="p-6">
              <h2 className="text-lg font-semibold mb-4">Répartition par type de référence</h2>
              <ul className="space-y-2">
                {Object.entries(byTypeLabel).map(([label, value]) => (
                  <li key={label} className="flex items-center justify-between">
                    <span>{label}</span>
                    <span className="font-semibold">{value.toLocaleString('fr-FR', { style: 'currency', currency: 'MAD' })}</span>
                  </li>
                ))}
              </ul>
            </ModernCard>
          </div>
          <ModernCard className="p-6 mt-8 shadow-lg rounded-xl bg-white border border-neutral-200">
            <h2 className="text-lg font-semibold mb-4">Paiements récents</h2>
            <ul className="divide-y divide-neutral-200">
              {payments.slice(0, 8).map((p) => (
                <li key={p.id} className="py-2 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <span className="font-medium w-32 truncate">{p.client_name || '-'}</span>
                  <span className="w-32 truncate">{p.reference_type_label || p.reference_type} #{p.reference_id}</span>
                  <span className="w-32 truncate">{p.store_name || '-'}</span>
                  <span className="w-28 font-semibold text-primary-700">{Number(p.amount).toLocaleString('fr-FR', { style: 'currency', currency: 'MAD' })}</span>
                  <span className="w-24">{p.mode}</span>
                  <span className="w-24 text-neutral-500">{p.payment_date?.slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          </ModernCard>
        </>
      )}
    </div>
  );
};

export default PaiementsVueDEnsemble; 